/**
 * Configuration des routes
 */

import * as VueRouter from 'vue-router';
/***/
import { useHistoryStore } from '@stores/history';
import { useAuthUserStore } from '@stores/auth';
import { useUserStore } from '@stores/user';
/***/
import { useManagingUser } from '@compositions/ManagingUser';

const routes = [
    {
        path: '/',
        name: 'home',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Home'))
    },
    {
        path: '/privacy-policy',
        name: 'privacy-policy',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/PrivacyPolicy'))
    },
    {
        path: '/testing',
        name: 'testing',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Testing'))
    },
    // Authentification
    {
        path: '/auth/login',
        name: 'auth.login',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Auth/Login'))
    },
    {
        path: '/auth/register/:userType(agency)?',
        name: 'auth.register',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Auth/Register'))
    },
    {
        path: '/auth/forgot-password',
        name: 'auth.forgot-password',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Auth/ForgotPassword'))
    },
    {
        path: '/auth/reset-password',
        name: 'auth.reset-password',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Auth/ResetPassword'))
    },
    // Définition du mot de passe
    {
        path: '/auth/invitation/:publicId([^\/]+)',
        name: 'auth.invitation',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Auth/Invitation'))
    },

    // Validation des sujets de publication
    {
        path: '/me/subjects/validation/:validationToken([^\/]+)',
        name: 'me.subjects.validation',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Subjects/Validation'))
    },

    // Demande de congés de l'utilisateur connecté
    {
        path: '/me/holidays-requested',
        name: 'me.holidays-requested.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/HolidaysRequested'))
    },

    // Achat de crédits
    {
        path: '/me/credits',
        name: 'me.credits.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Credits'))
    },

    // Page d'abonnement
    {
        path: '/me/subscription',
        name: 'me.subscription.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Credits'))
    },

    // Gestion des paramètres du compte
    {
        path: '/me/account',
        name: 'me.account.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Account'))
    },

    // Paramètres du style rédactionnel
    {
        path: '/me/account/writing-styles',
        name: 'me.account.writing-styles',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Account/WritingStyles.vue'))
    },

    // Identité visuelle
    {
        path: '/me/account/visual-identity',
        name: 'me.account.visual-identity',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Account/VisualIdentity.vue'))
    },

    // Paramètres des publications
    {
        path: '/me/account/posts-settings',
        name: 'me.account.posts-settings',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Account/PostsSettings.vue'))
    },

    // Liste des factures
    {
        path: '/me/account/bills',
        name: 'me.account.bills.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Account/Bills/Index'))
    },

    // Statistiques du client connecté
    {
        path: '/me/statistics',
        name: 'me.statistics.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Statistics'))
    },

    // Utilisateurs (contacts) de l'agence
    {
        path: '/agency/users',
        name: 'me.agency.users.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/Users'))
    },

    // Initialisation des paramètres de l'IA pour le client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/init',
        name: 'me.agency.customers.init',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerInit'))
    },

    // Gestion du compte d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/account',
        name: 'me.agency.customers.account',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerAccount'))
    },

    // Gestion du style rédactionnel d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/account/writing-styles',
        name: 'me.agency.customers.account.writing-styles',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerAccountWritingStyles.vue'))
    },

    // Gestion de l'identité visuelle d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/account/visual-identity',
        name: 'me.agency.customers.account.visual-identity',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerAccountVisualIdentity.vue'))
    },

    // Édition de l'abonnement d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/subscription',
        name: 'me.agency.customers.subscription',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerSubscription'))
    },

    // Statistiques d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/statistics',
        name: 'me.agency.customers.statistics',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerStatistics'))
    },

    // Templates de chartes graphiques d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/graphical-line-templates',
        name: 'me.agency.customers.graphical-line-templates',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerGraphicalLineTemplates'))
    },

    // Personnalisation des posts d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/posts-settings',
        name: 'me.agency.customers.posts-settings',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerPostsSettings'))
    },

    // Tâches d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/tasks',
        name: 'me.agency.customers.tasks.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/AgencyCustomerTasksList'))
    },

    // Edition de la tâche d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/tasks/:taskPublicId([^\/]+)',
        name: 'me.agency.customers.tasks.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/AgencyCustomerTaskEdit'))
    },

    // Commentaires des publications d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/publications-comments',
        name: 'me.agency.customers.publications-comments.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerPublicationsComments.vue'))
    },

    // Fichiers du répertoire d'un client d'une agence
    {
        path: '/agency/customers/:agencyCustomerId([0-9]+)/repository/files',
        name: 'me.agency.customers.repository-files.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Agency/CustomerRepositoryFiles'))
    },

    // Gestion du répertoire de fichiers de l'utilisateur connecté
    {
        path: '/me/repository/files',
        name: 'me.repository-files.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/RepositoryFiles.vue'))
    },

    // Publications de l'utilisateur
    {
        path: '/me/tasks',
        name: 'me.tasks.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/List'))
    },
    {
        path: '/me/tasks/:taskPublicId([^\/]+)',
        name: 'me.tasks.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/Edit'))
    },

    // Tâches en attente de validation
    {
        path: '/me/waiting-validation-tasks',
        name: 'me.waiting-validation-tasks.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/Validation/List'))
    },

    // Page de retour d'une tâche
    {
        path: '/me/tasks/:taskPublicId([^\/]+)/return',
        name: 'me.tasks.return',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/CustomerReturn'))
    },

    {
        path: '/me/tasks/write',
        name: 'me.tasks.write',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/Write'))
    },

    // Page de la liste des commentaires des réseaux sociaux
    {
        path: '/me/publications-comments',
        name: 'me.publications-comments.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/PublicationsComments/List'))
    },

    // Tâches pour l'agent connecté
    {
        path: '/me/running-tasks',
        name: 'me.running-tasks.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/Running/List'))
    },
    {
        path: '/me/running-tasks/:taskPublicId([^\/]+)/processing',
        name: 'me.running-tasks.processing',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/Running/Processing'))
    },

    // Tâches pour le manager connecté
    {
        path: '/me/managing-tasks',
        name: 'me.managing-tasks.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Me/Tasks/Managing/List'))
    },

    // Options de l'application pour le super admin
    {
        path: '/admin/master',
        name: 'admin.super-admin',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/SuperAdmin/Settings'))
    },

    // Test de requête pour le SuperAdmin
    {
        path: '/admin/master/ai-test',
        name: 'admin.super-admin.ai-test',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/SuperAdmin/AITest'))
    },

    // Gestion des permissions
    {
        path: '/admin/permissions/sections',
        name: 'admin.permissions.sections.list',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Permission/Section/List'))
    },
    {
        path: '/admin/permissions/sections/:sectionId([0-9]+)',
        name: 'admin.permissions.sections.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Permission/Section/Edit'))
    },

    // Gestion des rôles
    {
        path: '/admin/roles',
        name: 'admin.roles.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Role/List'))
    },
    {
        path: '/admin/roles/add',
        name: 'admin.roles.add',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Role/Add'))
    },
    {
        path: '/admin/roles/:roleId([0-9]+)',
        name: 'admin.roles.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Role/Edit'))
    },

    // Personnalisation de l'application
    {
        path: '/admin/application',
        name: 'admin.application.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/CustomizeApplication'))
    },

    // Gestion de la configuration des mails
    {
        path: '/admin/mails',
        name: 'admin.mails.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Mail/List'))
    },

    // Gestion de l'administration des crédits
    {
        path: '/admin/credits',
        name: 'admin.credits.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Credits'))
    },

    // Gestion des contacts
    {
        path: '/admin/contacts',
        name: 'admin.contacts.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Contact/List'))
    },

    // Gestion des secteurs d'activités
    {
        path: '/admin/activity-sectors',
        name: 'admin.activity-sectors.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/ActivitySector/List'))
    },
    {
        path: '/admin/activity-sectors/create',
        name: 'admin.activity-sectors.create',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/ActivitySector/Create'))
    },
    {
        path: '/admin/activity-sectors/:sectorId([0-9]+)',
        name: 'admin.activity-sectors.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/ActivitySector/Edit'))
    },

    // Gestion des messages de désabonnement
    {
        path: '/admin/unsubscribe-messages',
        name: 'admin.unsubscribe-messages.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/UnsubscribeMessage/List'))
    },

    // Gestion des groupes de tâches
    {
        path: '/admin/videos',
        name: 'admin.videos.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Videos/List'))
    },

    // Gestion des idées de publications
    {
        path: '/admin/publication-ideas',
        name: 'admin.publication-ideas.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/PublicationIdeas/List'))
    },

    // Gestion du marronnier
    {
        path: '/admin/horse-chestnut',
        name: 'admin.horse-chestnut.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/HorseChestnut/Wrapper'))
    },

    // Gestion des groupes de tâches
    {
        path: '/admin/task-configurations/groups',
        name: 'admin.task-configurations.groups.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/TaskConfigurations/Groups/List'))
    },

    // Gestion des utilisateurs
    {
        path: '/admin/users/:userType([^\/]+)',
        name: 'admin.users.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/User/List'))
    },
    {
        path: '/admin/users/:userType([^\/]+)/create',
        name: 'admin.users.create',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/User/Create'))
    },
    {
        path: '/admin/users/:userType([^\/]+)/:userId([0-9]+)/edit',
        name: 'admin.users.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/User/Edit'))
    },

    // Gestion des offres de coaching
    {
        path: '/admin/coaching-offers',
        name: 'admin.coaching-offers.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/CoachingOffer/List'))
    },
    {
        path: '/admin/coaching-offers/create',
        name: 'admin.coaching-offers.create',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/CoachingOffer/Create'))
    },
    {
        path: '/admin/coaching-offers/:offerId([0-9]+)/edit',
        name: 'admin.coaching-offers.edit',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/CoachingOffer/Edit'))
    },

    // Demande de congés reçus par l'utilisateur connecté
    {
        path: '/admin/users/holidays-requested',
        name: 'admin.users.holidays-requested.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/User/HolidaysRequested'))
    },

    // Code de promotion
    {
        path: '/admin/promotion-codes',
        name: 'admin.promotion-codes.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/PromotionCodes/List'))
    },

    // Liste des factures des clients
    {
        path: '/admin/bills',
        name: 'admin.bills.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Bills/List'))
    },

    // Gestion des vidéos de tutorial
    {
        path: '/admin/videos/tutorial',
        name: 'admin.videos.tutorial.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/Videos/Tutorial/List'))
    },

    // Liste des templates des chartes graphiques
    {
        path: '/admin/graphical-line-templates',
        name: 'admin.graphical-line-templates.index',
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Admin/GraphicalLineTemplate/List'))
    },

    // Pages d'erreur
    {
        path: '/error/:statusCode([0-9]+)',
        name: 'error',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Error'))
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'error.not-found',
        meta: { requiresAuth: false },
        component: (() => import(/* webpackChunkName: "routes" */ '@components/Pages/Error'))
    }
];

const router = VueRouter.createRouter({
    history: VueRouter.createWebHistory(),
    routes,
    scrollBehavior (to, from, savedPosition) {

        const historyStore = useHistoryStore();

        if(savedPosition) {
            historyStore.setMustPreserveScrollPosition(false);
            return savedPosition;
        }

        const preserveScrollPosition = historyStore.getMustPreserveScrollPosition;

        // Evite d'avoir un scrollToTop lorsqu'on ne le souhaite pas (une query qui change en get par exemple)
        if (preserveScrollPosition) {
            historyStore.setMustPreserveScrollPosition(false);
            return {};
        }

        return {
            el: '#app',
            behavior: 'smooth'
        };
    }
});



router.beforeEach( async (to, from) => {

    const authUserStore = useAuthUserStore();

    // Chargement de l'utilisateur
    await authUserStore.getCurrentUser();

    // Composition pour modifier l'utilisateur à gérer
    const { getManagingUser, setManagingUser } = useManagingUser({});

    // Affecte l'utilisateur à gérer seulement si le chemin de l'URL change (on ignore les query)
    const userAuthenticated = authUserStore.authenticated;
    const authUser = (userAuthenticated) ? authUserStore.getUser : null;
    const toPath = to.path;
    const fromPath = from.path;
    if(getManagingUser.value === null || toPath !== fromPath) {
        setManagingUser(authUser);
    }

    // Redirection vers la page d'authentification si l'utilisateur n'est pas connecté
    const requiredAuth = (to?.meta?.requiresAuth ?? true);

    const guestAllowed = (requiredAuth === false);

    if(! guestAllowed && ! userAuthenticated) {
        if (requiredAuth !== false) {
            const historyStore = useHistoryStore();
            historyStore.setPrevious(to);
        }
        return {
            name: 'auth.login'
        };
    }

    return;
});

export default router;
